'use client';

import { useSearchParams } from 'next/navigation';
import React, { useEffect, useMemo, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import Button from '@/components/molecules/button/button';
import { SliderImageItem } from '@/components/organisms/image-slider/image-slider';
import { ProductCardProps } from '@/components/organisms/product-card/product-card';
import FavoritesSlideIn from '@/components/templates/favorites/favorites-slide-in/favorites-slide-in';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Row from '@/components/utilities/row/row';
import useQueryFavoritesHotelsWithOffers from '@/integrations/offers/favorites/hotels-with-offers/hotels-with-offers.hooks';
import useQueryRetrieveSharedFavorites from '@/integrations/offers/favorites/share/retrieve-hotels-with-offers/retrieve-hotels-with-offers.hooks';
import useQueryHotelsPictures from '@/integrations/offers/hotels/pictures/pictures.hooks';
import {
  trackPageLoadEvent,
  trackViewEvent,
} from '@/integrations/tracking/google-tag-manager/scripts';
import { tealiumView } from '@/integrations/tracking/tealium/scripts';
import { useFavoritesContext } from '@/libraries/contexts/favorites-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import FavoritesCardList from './favorites-page-results/favorites-card-list';
import styles from './favorites.module.scss';

export interface FavoritesProps {
  /** Headline for the favorites page */
  headline?: string;
  /** Personalization data for the user */
  personalizationData?: Content.Properties.PersonalizationData;
  /** Pre-headline for the favorites page */
  preHeadline?: string;
  /** The body text of the composed email */
  shareFavoritesEmailBody?: string;
  /** The subject text of the composed email */
  shareFavoritesEmailSubject?: string;
  /** Headline for the favorites share slide page */
  shareFavoritesHeadline?: string;
  /** Text for the favorites share slide page */
  shareFavoritesText?: string;
  /** Headline for the shared favorites page */
  sharedUserHeadline?: string;
  /** Text for the favorites page */
  text?: string;
  /** Tracking data for the page */
  trackingData?: Content.Properties.TrackingData;
}

interface InteractedItem {
  hotelId: number | null;
  offerId: string | null;
  pictures: Content.Properties.HotelPicture[];
}

interface ExpiredOffers {
  own: string[];
  shared: string[];
}

type FavoritesMode = 'own' | 'shared' | undefined;

const DEFAULT_IMAGE_NUMBER = 4;

const Favorites = ({
  headline,
  personalizationData,
  preHeadline,
  shareFavoritesEmailBody,
  shareFavoritesEmailSubject,
  shareFavoritesHeadline,
  sharedUserHeadline,
  shareFavoritesText,
  text,
  trackingData,
}: FavoritesProps) => {
  const { t } = useI18n();
  const searchParams = useSearchParams();
  const currencyFormatter = useCurrencyFormatter();

  const { favoriteOffers, removeFavoriteOffer, addFavoriteOffer, isFavorite } =
    useFavoritesContext();

  const [showWarning, setShowWarning] = useState(false);
  const [offerIds, setOfferIds] = useState<string[]>([]);
  const [favoritesKey, setFavoritesKey] = useState<string>('');
  const [showFavoritesSlideIn, setShowFavoritesSlideIn] = useState(false);
  const [favoritesMode, setFavoritesMode] = useState<FavoritesMode>(undefined);
  const [interactedItems, setInteractedItems] = useState<InteractedItem[]>([]);
  const [isSharedFavoritesAddedAsOwn, setIsSharedFavoritesAddedAsOwn] =
    useState(false);
  const [expiredOffers, setExpiredOffers] = useState<ExpiredOffers>({
    own: [],
    shared: [],
  });
  const [interactedItem, setInteractedItem] = useState<InteractedItem>({
    hotelId: null,
    offerId: null,
    pictures: [],
  });

  const {
    data: dataFavoritesHotelsWithOffers,
    isFetching: isFetchingFavoritesHotelsWithOffers,
  } = useQueryFavoritesHotelsWithOffers(
    { offerIds },
    { enabled: !!offerIds.length },
  );
  const {
    data: dataRetrieveSharedFavorites,
    isFetching: isFetchingRetrieveSharedFavorites,
  } = useQueryRetrieveSharedFavorites(
    { key: favoritesKey },
    { enabled: !!favoritesKey },
  );
  const { data: dataHotelsPictures } = useQueryHotelsPictures({
    hotelId: interactedItem.hotelId,
  });

  const handleClickFavorite = (offerId: string) => {
    if (isFavorite(offerId)) {
      removeFavoriteOffer(offerId);
    } else {
      addFavoriteOffer(offerId);
    }
  };

  const addSharedFavoritesAsOwn = () => {
    if (!offerIds) return;

    offerIds
      .filter(offerId => !expiredOffers.shared.includes(offerId))
      .filter(offerId => !isFavorite(offerId))
      .forEach(offerId => addFavoriteOffer(offerId));

    setIsSharedFavoritesAddedAsOwn(true);
  };

  const setupSharedFavoritesState = () => {
    if (!dataRetrieveSharedFavorites) return;

    const sharedOfferIds =
      dataRetrieveSharedFavorites.favoriteIds?.split(',') || [];

    setOfferIds(sharedOfferIds);

    const hasDistinctOffers = sharedOfferIds
      .filter(offerId => !expiredOffers.shared.includes(offerId))
      .some(sharedOfferId => !isFavorite(sharedOfferId));

    setIsSharedFavoritesAddedAsOwn(!hasDistinctOffers);
  };

  useEffect(() => {
    trackPageLoadEvent({ content_group: 'favorites' });

    personalizationData && tealiumView(personalizationData);

    if (!trackingData?.gtmDataLayer) return;

    trackViewEvent(trackingData.gtmDataLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchParams!.get('key')) {
      setFavoritesKey('');
      setFavoritesMode('own');

      return;
    }

    setFavoritesMode('shared');
    setupSharedFavoritesState();
    setFavoritesKey(searchParams!.get('key') as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams!.get('key')]);

  useEffect(() => {
    if (favoritesMode !== 'own') return;

    setOfferIds(favoriteOffers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteOffers, favoritesMode]);

  useEffect(() => {
    if (favoritesMode !== 'shared') return;

    setupSharedFavoritesState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataRetrieveSharedFavorites, favoriteOffers]);

  useEffect(() => {
    if (!favoritesMode) return;

    const showWarning =
      (favoritesMode === 'own' && expiredOffers.own.length > 0) ||
      (favoritesMode === 'shared' && expiredOffers.shared.length > 0);

    setShowWarning(showWarning);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiredOffers, favoritesMode]);

  useEffect(() => {
    if (!favoritesMode) return;

    if (!dataFavoritesHotelsWithOffers) return;

    if (dataFavoritesHotelsWithOffers?.unmatchedOfferUniqueIds.length > 0) {
      setExpiredOffers(prev => ({
        ...prev,
        [favoritesMode]: dataFavoritesHotelsWithOffers.unmatchedOfferUniqueIds,
      }));

      // remove expired/invalid offers form local storage
      dataFavoritesHotelsWithOffers.unmatchedOfferUniqueIds.forEach(
        removeFavoriteOffer,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFavoritesHotelsWithOffers]);

  useEffect(() => {
    if (dataHotelsPictures) {
      setInteractedItems(prevHotelsData => {
        return prevHotelsData.map(hotel =>
          hotel.offerId === interactedItem.offerId
            ? ({
                ...hotel,
                pictures: dataHotelsPictures,
              } as unknown as InteractedItem)
            : hotel,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataHotelsPictures]);

  const formattedSearchResults = useMemo((): ProductCardProps[] => {
    if (!dataFavoritesHotelsWithOffers?.result || offerIds.length === 0)
      return [];

    return dataFavoritesHotelsWithOffers.result.map(
      (item: Offers.Properties.SearchResult): ProductCardProps => {
        const combinedPictures = [
          ...item.pictures, // Keep the original 4 pictures
          ...(
            interactedItems.find(
              interactedItem =>
                interactedItem.offerId === item.bestOfferSummary?.uniqueId,
            )?.pictures || []
          ).slice(DEFAULT_IMAGE_NUMBER), // Add new pictures except the first 4
        ];

        const images: SliderImageItem[] = combinedPictures.map(picture => ({
          altText: picture.description || '',
          url: picture.url,
        }));

        const tags = item.facilityHighlights || [];
        const imageTags = item.facilityTags || [];

        const bestOfferSummary = item.bestOfferSummary;

        const price = currencyFormatter(
          bestOfferSummary?.price.totalPrice as number,
        );

        let priceLabelAmount: string;

        if (bestOfferSummary?.childrenNo && bestOfferSummary?.childrenNo > 0) {
          priceLabelAmount = `${bestOfferSummary?.adultsNo} ${t('items.search.searchBar.persons.adults')}, ${bestOfferSummary?.childrenNo} ${t('items.search.searchBar.persons.children')}`;
        } else {
          priceLabelAmount = `${bestOfferSummary?.adultsNo} ${t('items.search.searchBar.persons.adults')}`;
        }

        const priceLabel = t('items.search.productCard.totalPrice', {
          persons: priceLabelAmount,
        });

        const offerAmount = currencyFormatter(
          bestOfferSummary?.price.normalPrice as number,
        );

        const discount =
          bestOfferSummary?.price.discount !== 0
            ? currencyFormatter(bestOfferSummary?.price.discount as number)
            : '0';

        const transportMode = item.bestOfferSummary?.transportMode;

        const getIsFavorite = (uniqueId?: string): boolean => {
          if (!uniqueId) return false;

          if (favoritesMode === 'shared') {
            return isFavorite(uniqueId);
          }

          return true;
        };

        return {
          departureName: bestOfferSummary?.departureName ?? undefined,
          description: bestOfferSummary?.description,
          duration: `${bestOfferSummary?.daysDuration.toString()} ${t('items.search.productCard.nights')}`,
          highlightScheduled: bestOfferSummary?.highlightScheduled ?? undefined,
          homeboundDepartureDate:
            bestOfferSummary?.homeboundDepartureDate ?? undefined,
          homeboundDepartureName:
            bestOfferSummary?.homeboundDepartureName ?? undefined,
          hotelId: item.hotelId,
          hotelName: item.hotelName,
          images,
          imageTags,
          includedProducts: item.bestOfferSummary?.includedProducts,
          isFavorite: getIsFavorite(item.bestOfferSummary?.uniqueId),
          location: item.location,
          middleSection: bestOfferSummary?.middleSection,
          offerAmount: discount !== '0' ? offerAmount : '',
          offerId: item.bestOfferSummary?.offerId,
          offerTag: '',
          onFetchMoreImages: () => {
            const interactedItem = {
              hotelId: item.hotelId,
              offerId: item.bestOfferSummary?.uniqueId || null,
              pictures: [],
            } as InteractedItem;

            setInteractedItem(interactedItem);
            setInteractedItems(previousValues => {
              return [...previousValues, interactedItem];
            });
          },
          outboundArrivalDate:
            bestOfferSummary?.outboundArrivalDate ?? undefined,
          outboundDepartureDate:
            bestOfferSummary?.outboundDepartureDate ?? undefined,
          outboundDepartureName:
            bestOfferSummary?.outboundDepartureName ?? undefined,
          price:
            price ??
            item.roomConfigurations[0].offers[0].price.totalPrice.toString(),
          priceDetails: item.bestOfferSummary
            ?.detailsModal as Content.Components['PriceDetailsDto'],
          priceDiscount:
            discount !== '0'
              ? `${t('items.search.productCard.save')} ${discount}`
              : '0',
          priceLabel,
          rating: item.stars as number,
          showHeartHotelLogo: item.isHeartHotel,
          specialHotelType: item.specialHotelType,
          tags,
          transportMode,
          uniqueId: item.bestOfferSummary?.uniqueId,
          urgencyTags: item.bestOfferSummary?.urgencyTags,
          url: item.bestOfferSummary?.searchUrl ?? item.url,
        };
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataFavoritesHotelsWithOffers?.result,
    favoriteOffers,
    offerIds,
    interactedItems,
  ]);

  const getShareFavoritesSection = () => {
    if (
      !offerIds?.length ||
      isFetchingFavoritesHotelsWithOffers ||
      isFetchingRetrieveSharedFavorites
    )
      return;

    if (favoritesMode === 'own') {
      return (
        <div className={styles.shareButton}>
          <Button
            onClick={() => setShowFavoritesSlideIn(true)}
            text={t('items.favorites.shareFavorites')}
            variant="tertiary"
          />
        </div>
      );
    }

    if (isSharedFavoritesAddedAsOwn) {
      return (
        <div className={styles.infoWrapper}>
          <Button
            className={styles.infoButton}
            icon="CheckMark"
            text={t('items.favorites.addSharedFavoritesMessage')}
            variant="secondary"
          />
        </div>
      );
    }

    return (
      <div className={styles.addAsOwnButton}>
        <Button
          onClick={() => addSharedFavoritesAsOwn()}
          text={t('items.favorites.addSharedFavorites')}
          variant="secondary"
        />
      </div>
    );
  };

  const getHeaderContent = () => {
    if (favoritesMode === 'own') {
      return (
        <React.Fragment>
          {(preHeadline || headline || text) && (
            <div className={styles.header}>
              {preHeadline && (
                <Typography
                  className={styles.preHeadline}
                  tagStyle="labelMedium"
                >
                  {preHeadline}
                </Typography>
              )}

              {headline && (
                <Typography
                  className={styles.headline}
                  tagStyle="displayMedium"
                >
                  {headline}
                </Typography>
              )}

              {text && <RichText text={text} />}
            </div>
          )}
        </React.Fragment>
      );
    }

    if (favoritesMode === 'shared') {
      return (
        <Typography className={styles.headline} tagStyle="displayMedium">
          {sharedUserHeadline}
        </Typography>
      );
    }
  };

  return (
    <div className={styles.component}>
      <Container>
        <Row>
          <Column>
            <div className={styles.pageHeader}>
              {getHeaderContent()}

              {getShareFavoritesSection()}
            </div>
          </Column>
        </Row>

        {showWarning && favoritesMode && (
          <Row>
            <Column>
              <AlertBanner
                className={styles.alertBanner}
                text={t('items.favorites.expiredOffers', {
                  offerNo: expiredOffers[favoritesMode].length,
                })}
              />
            </Column>
          </Row>
        )}

        <Row>
          <Column>
            <FavoritesCardList
              favorites={formattedSearchResults}
              isLoading={
                isFetchingFavoritesHotelsWithOffers ||
                isFetchingRetrieveSharedFavorites
              }
              onClickFavorite={handleClickFavorite}
            />
          </Column>
        </Row>

        <FavoritesSlideIn
          emailBody={shareFavoritesEmailBody}
          emailSubject={shareFavoritesEmailSubject}
          headline={shareFavoritesHeadline}
          isOpen={showFavoritesSlideIn}
          setIsOpen={setShowFavoritesSlideIn}
          text={shareFavoritesText}
        />
      </Container>
    </div>
  );
};

Favorites.displayName = 'Favorites';

export default Favorites;
